import { format, isValid, parse } from 'date-fns';
import React from 'react';

import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { useInlineEdit } from '../hooks/useInlineEdit';
import { InfoBadge } from './InfoBadge';
import { InlineEditControls } from './InlineEditControls';

interface FiledDateEditBadgeProps {
  matterId: string;
  value: string | undefined | null;
  className?: string;
}

const formatDateForDisplay = (
  dateString: string | undefined | null,
): string => {
  if (!dateString) return 'Not Set';

  try {
    // Try parsing as ISO date first (YYYY-MM-DD)
    const parsed = parse(dateString, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      return format(parsed, 'MM/dd/yyyy');
    }

    // If that fails, try direct Date parsing
    const date = new Date(dateString);
    if (isValid(date)) {
      return format(date, 'MM/dd/yyyy');
    }
  } catch {
    // Silent catch - return original string on error
  }

  return dateString;
};

export const FiledDateEditBadge: React.FC<FiledDateEditBadgeProps> = ({
  matterId,
  value,
  className,
}) => {
  const {
    isEditing,
    setIsEditing,
    editValue,
    setEditValue,
    handleSave,
    handleCancel,
    isLoading,
  } = useInlineEdit({
    matterId,
    initialValue: value || '',
    fieldName: 'filed_date',
    onSuccessMessage: 'Filed date updated',
    transformValue: (val) => val || null,
  });

  const displayValue = formatDateForDisplay(value);

  return (
    <Popover
      open={isEditing}
      onOpenChange={(open) => {
        if (!open) handleCancel();
        else setIsEditing(true);
      }}
    >
      <PopoverTrigger asChild>
        <InfoBadge
          label="Filed"
          value={`Filed: ${displayValue}`}
          onClick={() => setIsEditing(true)}
          showLabel={false}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent className="p-2 w-auto" align="start" side="bottom">
        <InlineEditControls
          isEditing={isEditing}
          onSave={handleSave}
          onCancel={handleCancel}
          isLoading={isLoading}
        >
          <div className="flex items-center gap-1">
            <span className="text-xs text-zinc-500 whitespace-nowrap">
              Filed:
            </span>
            <Input
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="h-6 text-xs w-32 px-1 py-0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
          </div>
        </InlineEditControls>
      </PopoverContent>
    </Popover>
  );
};
